import { useState } from 'react';
import { Link } from 'react-router';
import { formatDate, formatDateTime, formatEuro } from '@brimo/shared';
import { clsx } from '../../ds/clsx';
import { Banner, Button, Eyebrow, Pill, Skeleton, Textarea } from '../../ds/components';
import { errorMessage } from '../../lib/api';
import type { ApprovalDetail } from '../approvals/api';
import {
  agreementPdfHref,
  approveReservation,
  rejectReleaseRequest,
  rejectReservation,
  releaseReservation,
  useInvalidateReservations,
  useReservation,
  type ReservationItem,
} from './api';
import { reservationStatusVariants } from './labels';
import { RejectReservationDialog } from './RejectReservationDialog';
import styles from './ReservationApprovalPanel.module.css';

export interface ReservationApprovalPanelProps {
  approval: ApprovalDetail;
  /** Called after „Freigeben" / „Aufheben" / „Ablehnen" went through. */
  onDecided?: ((item: ReservationItem) => void) | undefined;
}

const approvalPill: Record<
  ApprovalDetail['status'],
  { label: string; variant: 'warning' | 'success' | 'danger' | 'neutral' }
> = {
  open: { label: 'offen', variant: 'warning' },
  approved: { label: 'freigegeben', variant: 'success' },
  rejected: { label: 'abgelehnt', variant: 'danger' },
  withdrawn: { label: 'zurückgezogen', variant: 'neutral' },
};

/**
 * Reservierung 08/09 inside the approval dialog: the check points, the agreement PDF, the note to the
 * advisor and „Freigeben" / „Ablehnen" (`POST /reservations/:id/approve|reject`); for a `release`
 * approval the request context with „Aufheben" (`/release`) and „Ablehnen" (`/release-request/reject`).
 */
export function ReservationApprovalPanel({ approval, onDecided }: ReservationApprovalPanelProps) {
  const reservationId = approval.refs.reservationId;
  const query = useReservation(reservationId);
  const invalidate = useInvalidateReservations();
  const [note, setNote] = useState('');
  const [rejecting, setRejecting] = useState(false);
  const [pending, setPending] = useState<'approve' | 'reject' | null>(null);
  const [error, setError] = useState<unknown>(null);
  const item = query.data;
  const isRelease = approval.type === 'release';

  async function run(action: 'approve' | 'reject', call: () => Promise<ReservationItem>) {
    setPending(action);
    setError(null);
    try {
      const result = await call();
      await invalidate();
      setRejecting(false);
      onDecided?.(result);
    } catch (cause) {
      setError(cause);
    } finally {
      setPending(null);
    }
  }

  function approve() {
    if (!item) return;
    if (isRelease) {
      const reason = item.releaseRequest?.reason?.trim() || approval.message?.trim() || 'Aufhebung beantragt';
      const body: { reason: string; note?: string } = { reason };
      if (note.trim()) body.note = note.trim();
      void run('approve', () => releaseReservation(item.id, body));
      return;
    }
    void run('approve', () => approveReservation(item.id, note.trim() || undefined));
  }

  function reject(body: { reason: string; note: string }) {
    if (!item) return;
    if (isRelease) {
      const payload: { reason: string; note?: string } = { reason: body.reason };
      if (body.note) payload.note = body.note;
      void run('reject', () => rejectReleaseRequest(item.id, payload));
      return;
    }
    void run('reject', () => rejectReservation(item.id, body));
  }

  if (reservationId === null) {
    return <Banner tone="danger">Diese Freigabe verweist auf keine Reservierung.</Banner>;
  }
  if (query.isPending) {
    return (
      <div className={styles.stack} aria-busy="true">
        <Skeleton width="60%" height={20} />
        <Skeleton width="45%" />
        <Skeleton height={120} />
      </div>
    );
  }
  if (query.isError || !item) {
    return (
      <Banner tone="danger">
        {query.error ? errorMessage(query.error) : 'Die Reservierung konnte nicht geladen werden.'}
      </Banner>
    );
  }

  const advisor = item.advisor?.name ?? item.startedBy.name;
  // The approvals module marks these types as „decided elsewhere"; the reservation's own flag decides here.
  const decidable = approval.status === 'open' && (isRelease ? item.canRelease : item.canApprove);
  const pill = approvalPill[approval.status];
  const checks = item.approval?.checks ?? [];
  const decided = approval.status !== 'open';

  return (
    <div className={styles.stack}>
      {error !== null && !rejecting ? <Banner tone="danger">{errorMessage(error)}</Banner> : null}
      {approval.status === 'open' && !(isRelease ? item.canRelease : item.canApprove) ? (
        <Banner tone="info">
          {approval.recipient.id === approval.requestedBy?.id
            ? 'Nur die empfangende Person oder ein Admin entscheidet.'
            : `Diese Freigabe entscheidet ${approval.recipient.name}.`}
        </Banner>
      ) : null}

      <div className={styles.head}>
        <div className={styles.headText}>
          <div className={styles.titleRow}>
            <Pill variant={pill.variant}>{pill.label}</Pill>
            <span className={styles.subline}>
              {item.unit.propertyName}
              {item.unit.priceCents !== null ? ` · ${formatEuro(item.unit.priceCents)}` : ''}
              {` · gestartet von ${item.startedBy.name} am ${formatDate(item.startedAt)}`}
            </span>
          </div>
        </div>
        {decidable ? (
          <span className={styles.headActions}>
            <Button variant="secondary" disabled={pending !== null} onClick={() => setRejecting(true)}>
              Ablehnen
            </Button>
            <Button loading={pending === 'approve'} onClick={approve}>
              {isRelease ? 'Aufheben' : 'Freigeben'}
            </Button>
          </span>
        ) : null}
      </div>

      {decided ? (
        <div className={styles.decision}>
          <Pill variant={pill.variant}>{pill.label}</Pill>
          <span className={styles.decisionMeta}>
            {approval.decidedBy ? `${approval.decidedBy.name} · ` : ''}
            {approval.decidedAt ? formatDateTime(approval.decidedAt) : ''}
            {approval.reason ? ` · ${approval.reason}` : ''}
            {approval.note ? ` · „${approval.note}“` : ''}
          </span>
        </div>
      ) : null}

      {isRelease ? (
        <div className={styles.requestBox}>
          <Eyebrow as="div">Antrag auf Aufhebung</Eyebrow>
          <div className={styles.requestText}>
            {item.releaseRequest
              ? `${item.releaseRequest.requestedByShort ?? 'Ein Nutzer'} · ${formatDateTime(item.releaseRequest.at)}${item.releaseRequest.reason ? ` · „${item.releaseRequest.reason}“` : ''}`
              : approval.message
                ? `${approval.requestedBy?.name ?? 'Ein Nutzer'} · „${approval.message}“`
                : `${approval.requestedBy?.name ?? 'Ein Nutzer'} · Antrag auf Aufhebung einer fremden Reservierung`}
          </div>
          <dl className={styles.rows}>
            <div className={styles.row}>
              <dt>Status</dt>
              <dd>
                <Pill variant={reservationStatusVariants[item.status]}>{item.statusLabel}</Pill>
              </dd>
            </div>
            <div className={styles.row}>
              <dt>Gestartet von</dt>
              <dd>{item.startedBy.name}</dd>
            </div>
            {item.signedAt ? (
              <div className={styles.row}>
                <dt>Unterschrieben</dt>
                <dd>
                  {formatDate(item.signedAt)}
                  {item.signaturePlace ? ` · ${item.signaturePlace}` : ''}
                </dd>
              </div>
            ) : null}
            {item.approval?.status === 'approved' && item.approval.at ? (
              <div className={styles.row}>
                <dt>Freigegeben</dt>
                <dd>
                  {item.approval.byShort ?? '—'} · {formatDate(item.approval.at)}
                </dd>
              </div>
            ) : null}
            <div className={styles.row}>
              <dt>Deal</dt>
              <dd>
                <Link to={`/deals/${item.deal.id}`}>
                  #{item.deal.number} · {item.deal.stageName}
                </Link>
              </dd>
            </div>
          </dl>
          <p className={styles.requestNote}>
            „Aufheben“ hebt die Reservierung auf: {item.unit.number} wird sofort frei, der Deal springt auf
            „Beratung“ zurück, {item.contact.name} erhält eine Bestätigung per E-Mail.
          </p>
        </div>
      ) : null}

      <div className={styles.grid}>
        <div>
          <Eyebrow as="div" className={styles.groupLabel}>
            Prüfung
          </Eyebrow>
          <ul className={styles.checks}>
            {checks.map((check) => (
              <li key={check.key} className={styles.check}>
                <span
                  className={clsx(
                    styles.checkDot,
                    check.state === 'done' ? styles.checkDone : styles.checkOpen,
                  )}
                  aria-hidden="true"
                />
                <span className={styles.checkText}>
                  <span className={styles.checkLabel}>{check.label}</span>
                  <span
                    className={clsx(styles.checkDetail, check.state === 'open' && styles.checkDetailOpen)}
                  >
                    {check.detail}
                  </span>
                </span>
              </li>
            ))}
            {checks.length === 0 ? (
              <li className={styles.check}>
                <span className={clsx(styles.checkDot, styles.checkOpen)} aria-hidden="true" />
                <span className={styles.checkText}>
                  <span className={styles.checkLabel}>Noch keine Prüfpunkte</span>
                  <span className={styles.checkDetail}>
                    {item.signedAt ? item.statusLabel : 'wartet auf Unterschrift'}
                  </span>
                </span>
              </li>
            ) : null}
          </ul>
        </div>
        <div className={styles.preview}>
          <iframe
            className={styles.previewFrame}
            src={`${agreementPdfHref(item.id)}#toolbar=0&navpanes=0`}
            title="Reservierungsvereinbarung · PDF"
          />
          <a href={agreementPdfHref(item.id)} target="_blank" rel="noreferrer" className={styles.previewLink}>
            Reservierungsvereinbarung · PDF
          </a>
        </div>
      </div>

      {decidable ? (
        <Textarea
          label={`Notiz an ${advisor} (optional)`}
          placeholder="Nur bei Ablehnung Pflicht"
          rows={2}
          value={note}
          onChange={(event) => setNote(event.target.value)}
        />
      ) : null}

      <RejectReservationDialog
        open={rejecting}
        reservation={item}
        initialNote={note}
        kind={isRelease ? 'release' : 'reservation'}
        pending={pending === 'reject'}
        error={rejecting ? error : null}
        onClose={() => {
          setRejecting(false);
          setError(null);
        }}
        onSubmit={reject}
      />
    </div>
  );
}
